import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {
  DataTableComponent,
  OrderByExpression,
  Selectability,
  TableConfig
} from '../../../components/data-table/data-table.component';
import {StringFilterComponent} from "../../../components/data-table/filters/string-filter/string-filter.component";
import {firstValueFrom} from "rxjs";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {TaskDialogComponent, TaskStatusDisplayValues} from "../dialog/task-dialog.component";
import {NumberFilterComponent} from "../../../components/data-table/filters/number-filter/number-filter.component";
import {ActivatedRoute} from "@angular/router";
import {
  DataTableFilterComponent,
  FilterEntry
} from "../../../components/data-table/filters/data-table-filter.component";
import {FilterUtils} from "../../../components/data-table/filters/filter";
import {
  Task,
  TaskPriority,
  TaskStatus,
  TasksV1Service,
  TaskType
} from "../../../apis/backend";
import {FilterOption} from "../../../utils/types";
import {UserService} from "../../../services/user.service";
import {TaskTypeDialogComponent} from "../type-dialog/task-type-dialog.component";
import {DefaultPriorityColor, PriorityColorMap} from "../utils/priority";
import {DataTableCellDirective} from "../../../components/data-table/cell/data-table-cell.component";
import {ListFilterComponent} from "../../../components/data-table/filters/list-filter/list-filter.component";
import {DateFilterComponent} from "../../../components/data-table/filters/date-filter/date-filter.component";

@Component({
  selector: 'task-overview',
  templateUrl: './task-overview.component.html',
})
export class TaskOverviewComponent implements OnInit, AfterViewInit {
  
  public TableConfig: TableConfig = {
    ODataResourceName: 'tasks',
    Selectability: Selectability.SingleSelect,
    Columns: [
      {
        DisplayName: 'ID',
        PropertyName: 'TaskID',
        FilterType: NumberFilterComponent,
        Formatter: (v, row) => {
          let ret = document.createElement('a');
          ret.href = '/tasks/' + row['TaskID'];
          ret.target = '_blank';
          ret.innerText = v;
          return ret;
        }
      },
      {
        DisplayName: 'Name',
        PropertyName: 'Name',
        FilterType: StringFilterComponent
      },
      {
        DisplayName: 'Company',
        PropertyName: 'CompanyName',
        FilterType: StringFilterComponent
      },
      {
        DisplayName: 'Assigned To',
        PropertyName: 'AssignedToEmployee',
        Formatter: (v) => v ? v.FirstName + ' ' + v.LastName : ''
      },
      {
        DisplayName: 'Task Type',
        PropertyName: 'TaskType',
        SortProperty: 'TaskType/Name',
        FilterProperty: 'TaskType/TaskTypeID',
        FilterType: ListFilterComponent,
        Formatter: (v: TaskType) => v ? v.Name : ''
      },
      {
        DisplayName: 'Task Status',
        PropertyName: 'TaskStatus',
        Formatter: (v: TaskStatus) => v ? TaskStatusDisplayValues[v] : ''
      },
      {
        DisplayName: 'Priority',
        PropertyName: 'TaskPriority',
        SortProperty: 'TaskPriority/Priority',
        Formatter: (v: TaskPriority) => v
            ? DataTableCellDirective.ColorIndicatorFormatter(
              PriorityColorMap[v.TaskPriorityID] || DefaultPriorityColor, '', v.Name)
            : ''
      },
      {
        DisplayName: 'Due Today?',
        PropertyName: 'IsDueToday',
        Formatter: (v) => DataTableCellDirective.BooleanFormatter(v)
      },
      {
        DisplayName: 'Notes',
        PropertyName: 'Notes',
        FilterType: StringFilterComponent
      },
      {
        DisplayName: 'Create Date',
        PropertyName: 'CreateDate',
        FilterType: DateFilterComponent,
        Formatter: (v) => new Date(v).toLocaleDateString()
      }
    ],
    AnyFieldFilterPropertyNames: [
        'Name',
        'CompanyName',
        'Notes'
    ]
  }

  public PrefilterTemplates: FilterOption[] = [
    {
      Text: 'My Tasks (Active)',
      Value: 'AssignedToEmployee/EmployeeID eq {0} and TaskStatus ne \'Complete\''
    },
    {
      Text: 'My Tasks (All)',
      Value: 'AssignedToEmployee/EmployeeID eq {0}'
    },
    {
      Text: 'All Tasks (Active)',
      Value: 'TaskStatus ne \'Complete\''
    },
    {
      Text: 'All Tasks',
      Value: ''
    }
  ];

  public Prefilters = this.PrefilterTemplates;
  
  public Prefilter: string;
  public Filter: string;

  public PageIndex: number;
  public PageCount: number;
  
  public InitialFilters: FilterEntry[] = [];
  
  public OrderBy: OrderByExpression[];
  
  @ViewChild(DataTableComponent)
  public DataTable: DataTableComponent;

  @ViewChild(DataTableFilterComponent)
  public DataTableFilter: DataTableFilterComponent;

  public Ready = false;
  
  constructor(
      private _TaskService: TasksV1Service,
      private _modalService: NgbModal,
      private _route: ActivatedRoute,
      private _userService: UserService) {

    _TaskService.getTaskTypes().subscribe({
      next: (taskTypes) => {
        this.TableConfig.Columns.find(c => c.PropertyName == 'TaskType').FilterInputs =
            taskTypes.map(t => ({ Text: t.Name, Value: t.TaskTypeID }));
      }
    })
    
    _userService.CurrentUser.subscribe({
      next: (user) => {
        if (user != null) {
          this.Prefilters = this.PrefilterTemplates.map((f => ({
            Text: f.Text,
            Value: FilterUtils.format(f.Value, user.UserID)
          })));
        }
      }
    });
  }
  
  ngOnInit() {
    this.Prefilter = this.Prefilters[0].Value;
    this.OrderBy = [
      {
        Property: 'IsDueToday',
        Descending: true
      },
      {
        Property: 'TaskPriority/Priority',
        Descending: true
      },
      {
        Property: 'TaskID',
        Descending: true
      }
    ];
  }

  ngAfterViewInit() {
    Promise.resolve(null).then(() => {
      this.Ready = true;
    })
  }

  public CreateTask() {
    this._modalService.open(TaskDialogComponent, { size: 'lg' }).result
        .then((task: Task) => {
          if (task.TaskID) {
            return firstValueFrom(this._TaskService.updateTask(task.TaskID, task));
          }
          else {
            return firstValueFrom(this._TaskService.createTask(task));
          }
        })
        .then(() => {
          this.DataTable.Refresh();
        });
  }

  public EditTask(task?: Task) {
    if (!task) {
      task = this.DataTable.GetSelection();
    }
    if (task) {
      firstValueFrom(this._TaskService.getTask(task.TaskID))
          .then(task => {
            let modal = this._modalService.open(TaskDialogComponent, { size: 'lg' });
            modal.componentInstance.Task = task;
            modal.result
                .then((task: Task) => {
                  return firstValueFrom(this._TaskService.updateTask(task.TaskID, task));
                })
                .then(() => {
                  this.DataTable.Refresh();
                });
          });
    }
  }
  
  public ManageTaskTypes() {
    this._modalService.open(TaskTypeDialogComponent, { size: 'lg' }).result
        .then(() => {
          this.DataTable.Refresh();
        });
  }
}
